import React, { useMemo } from 'react';

/**
 * tabConfig 结构（由页面配置传入，仅包含动态标题）
 * @property titleMap - platform/country -> { titleEn, titleCn }
 */
export interface ITabConfig {
  titleMap?: Record<string, { titleEn: string; titleCn: string; }>;
}

export interface IPlatformCountryTableTransferProps {
  /** 当前 tab 的配置（仅 titleMap） */
  tabConfig: ITabConfig;
  /** contentHeader 中的 SegmentCo 配置 */
  segmentConfig?: Record<string, any>;
  /** 数据 key */
  dataKey?: string;
  data?: Record<string, any>;
  changeData?: (key: string, value: any) => void;
  children?: React.ReactNode;
  className?: string;
}

const DEFAULT_SEGMENT_CONFIG = {
  name: 'SegmentCo',
  props: {
    className: 'segment-light',
    style: { marginLeft: '10px' },
    config: {
      dataKey: 'platform_country_dimension',
      options: [
        { name: 'Platform', nameCn: '平台', value: 'platform', bubble: '', bubbleCn: '' },
        { name: 'Country', nameCn: '国家', value: 'country', bubble: '', bubbleCn: '' },
      ],
    },
  },
};

/**
 * Platform/Country 表格 Transfer 组件
 * 仅处理动态部分：根据 platform_country_dimension 计算标题、注入 contentHeader（SegmentCo）
 * 以及根据维度 prepend tableMetric 的 dimension key
 * CommonTable 的 componentMetrics 等配置直接放在 children 的 CommonTable 中
 */
const PlatformCountryTableTransfer = (props: IPlatformCountryTableTransferProps) => {
  const {
    tabConfig = {},
    segmentConfig = DEFAULT_SEGMENT_CONFIG,
    dataKey = 'platformCountryTableData',
    data,
    changeData,
    children,
    className,
  } = props;

  const platformCountryDimension = data?.platform_country_dimension || 'platform';
  const dim = platformCountryDimension === 'country' ? 'country' : 'platform';
  const dimKey = dim === 'country' ? 'country' : 'system';

  const { titleEn, titleCn } = useMemo(() => {
    const map = tabConfig?.titleMap || {};
    const titles = map[dim] || map.platform || { titleEn: '', titleCn: '' };
    return { titleEn: titles.titleEn, titleCn: titles.titleCn };
  }, [tabConfig?.titleMap, dim]);

  const metricsCardWrapProps = useMemo(
    () => ({
      titleEn,
      titleCn,
      contentHeader: segmentConfig ? [segmentConfig] : [],
    }),
    [titleEn, titleCn, segmentConfig],
  );

  return (
    <div className={className}>
      {React.Children.map(children, (child: any) => {
        if (!child) return null;
        // 配置化渲染时 children 通常是 <Render />，最终以 child.props.config.props 为准
        const childConfig = child?.props?.config;
        const childConfigProps = childConfig?.props || child?.props || {};
        const childCommonBaseTableProps = childConfigProps?.commonBaseTableProps || {};
        const childMetricsCardWrapProps = childConfigProps?.metricsCardWrapProps || {};

        const baseTableMetric = childCommonBaseTableProps.tableMetric || [];
        const tableMetric = [dimKey, ...baseTableMetric];

        // 方案 A：Transfer 只注入动态必需字段（title/contentHeader + tableMetric 前置维度列）
        // operation/link 等静态配置完全由 CommonTable 自己提供
        const mergedMetricsCardWrapProps = {
          ...childMetricsCardWrapProps,
          titleEn: metricsCardWrapProps.titleEn,
          titleCn: metricsCardWrapProps.titleCn,
          contentHeader: metricsCardWrapProps.contentHeader,
        };

        const mergedCommonBaseTableProps = { ...childCommonBaseTableProps, tableMetric };

        const nextChildConfig =
          childConfig
            ? {
              ...childConfig,
              props: {
                ...childConfigProps,
                dataKey,
                metricsCardWrapProps: mergedMetricsCardWrapProps,
                commonBaseTableProps: mergedCommonBaseTableProps,
              },
            }
            : undefined;

        return React.cloneElement(child, {
          dataKey,
          key: `${child.key || 'table'}-${platformCountryDimension}`, 
          data,
          changeData,
          ...(nextChildConfig ? { config: nextChildConfig } : {}),
          // 非配置化渲染（child.props 没有 config）时，直接透传合并后的 props
          ...(!nextChildConfig ? {
            metricsCardWrapProps: mergedMetricsCardWrapProps,
            commonBaseTableProps: mergedCommonBaseTableProps,
          } : {}),
        });
      })}
    </div>
  );
};

export default PlatformCountryTableTransfer;
